import React, { useState, useEffect, ChangeEvent } from "react";
import { useFormikContext } from "formik";

import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";

import FormField from "components/FormField";
import MUIAccordionCustomExpandIcon from "components/MUIAccordionCustomExpandIcon";
import { IModule } from "../constants";
import useStyles from "./styles";

interface IValidationResults {
  hasError: boolean;
  validationIcon: React.ReactNode;
}

interface IProps {
  moduleData: IModule;
  getValidationResults: (moduleName: string) => IValidationResults;
  onValueChange: (
    fieldName: string,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onFormValueChange: (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  shouldExpandBlock: boolean;
  onSetShouldExpandBlock: (value: boolean) => void;
}

const FormModule = (props: IProps) => {
  const {
    moduleData,
    getValidationResults,
    onValueChange,
    onFormValueChange,
    shouldExpandBlock,
    onSetShouldExpandBlock
  } = props;

  const classes = useStyles();
  const formik = useFormikContext();

  const formikValues = formik.values || {};
  const validationsResults = getValidationResults(moduleData.name);

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);

  const handleValueChange =
    (fieldName: string) =>
    async (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onValueChange(fieldName, event);
      onSetShouldExpandBlock(false);
    };

  const handleChangeAccordionState = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    expanded: boolean
  ) => {
    setIsAccordionExpanded(expanded);
    onSetShouldExpandBlock(false);
  };

  useEffect(() => {
    if (validationsResults?.hasError && shouldExpandBlock) {
      setIsAccordionExpanded(true);
    }
  }, [validationsResults?.hasError, shouldExpandBlock]);

  return (
    <Accordion
      className={classes.section}
      expanded={isAccordionExpanded}
      onChange={handleChangeAccordionState}
    >
      <AccordionSummary
        expandIcon={<MUIAccordionCustomExpandIcon />}
        classes={{
          root: classes.sectionHeader,
          expanded: classes.sectionHeaderExpanded,
          content: classes.sectionHeaderContent
        }}
      >
        {moduleData.title}
        {validationsResults?.validationIcon}
      </AccordionSummary>
      <AccordionDetails className={classes.fieldsGroup}>
        {moduleData.fields.map(field => (
          <FormField
            key={field.name}
            field={field}
            moduleName={moduleData.name}
            value={formikValues[field.name]}
            onChange={onFormValueChange}
            onChangeCustom={handleValueChange}
            error={formik.errors[field.name]}
            helperText={formik.errors[field.name] || ""}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(FormModule);
